/**
 * Frontend Rate Limiting Service
 * Tracks usage locally and provides UI feedback
 */

const STORAGE_KEY = 'rate_limit_usage';

class RateLimitService {
  constructor() {
    this.config = {
      enabled: true, // Frontend tracking always enabled
      userLimit: parseInt(process.env.REACT_APP_USER_RATE_LIMIT) || 5,
      ipLimit: parseInt(process.env.REACT_APP_IP_RATE_LIMIT) || 10,
      window: (parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW_SECONDS) || 3600) * 1000, // Convert to milliseconds
    };

    // Array of wellness messages for rate limit hits
    this.wellnessMessages = [
      "💧 Because smart breaks make sharper minds - grab a glass of water and let your creativity flow",
      "🧘‍♀️ Because the best insights come when you're refreshed - perfect moment for a energizing stretch",
      "🌱 Because fresh perspectives need fresh air - step outside and return with new ideas",
      "👀 Because clear vision leads to clear thinking - rest your eyes and see opportunities differently",
      "🚶‍♂️ Because movement sparks innovation - take a walk and let your mind wander to solutions",
      "☕ Because great decisions deserve great preparation - brew something delicious and think deeply",
      "🤸‍♀️ Because your body fuels your brilliance - energize yourself with some quick exercises",
      "🌞 Because natural light powers natural intelligence - soak up some sunshine for mental clarity",
      "🧠 Because calm confidence creates better outcomes - practice mindful breathing for peak performance",
      "💪 Because energy flows where focus goes - get your blood pumping for sharper thinking",
      "📚 Because inspiration is everywhere - discover something new while your assessment processes",
      "🎵 Because rhythm enhances thinking - put on music and let your mind sync with success",
      "🍎 Because nourished minds make better choices - fuel your brain for optimal decision-making",
      "🤲 Because self-care amplifies success - treat your hands and body with the care they deserve",
      "🌊 Because awareness multiplies opportunities - practice mindfulness to spot what others miss"
    ];

    // Array of engine/startup messages
    this.engineMessages = [
      "Because quality assessments require thoughtful processing - we're crafting your perfect analysis",
      "Because excellence takes precision - we're ensuring every detail in your reports will become better each time",
      "Because your business deserves the best insights - we're optimizing every aspect of your future assessments",
      "Because thoroughness creates value - we're gathering comprehensive data for your success",
      "Because smart algorithms need time to work their magic - we're analyzing all available information",
      "Because personalized results require careful attention - we're tailoring everything specifically for you",
      "Because deep insights come from careful analysis - we're processing multiple data sources for accuracy",
      "Because exceptional service means taking time to get it right - we're ensuring premium quality results"
    ];

    // Array of growth milestone messages
    this.milestoneMessages = [
      "Because you're investing in the future of risk intelligence - every assessment makes our platform smarter ⚡",
      "Because smart businesses choose growing platforms - you're getting better results as we scale 🚀",
      "Because early adopters get the best rewards - you're building value alongside our innovation",
      "Because your success drives our improvement - each user makes our algorithms more powerful",
      "Because forward-thinking companies see opportunities others miss - you're ahead of the curve",
      "Because excellence is a journey we're taking together - your insights help us serve everyone better",
      "Because pioneers shape the future - you're helping create tomorrow's risk assessment standard",
      "Because smart partnerships create mutual success - we grow stronger as you grow stronger",
      "Because innovation happens through collaboration - your feedback drives our next breakthrough",
      "Because the best tools are built by understanding real needs - you're shaping our roadmap",
      "Because visionary users deserve visionary tools - we're building the platform you've been waiting for",
      "Because timing creates competitive advantage - you're positioning yourself for future success"
    ];
  }

  /**
   * Get current timestamp
   */
  now() {
    return Date.now();
  }

  /**
   * Get session-based deterministic index (changes every few hours)
   */
  getSessionBasedIndex(arrayLength, type = 'default') {
    // Create variation that changes every 4 hours but stays consistent within that period
    const now = new Date();
    const dateString = now.toDateString();
    const hourBlock = Math.floor(now.getHours() / 4); // Changes every 4 hours (0,1,2,3,4,5)
    const sessionString = `${dateString}-${hourBlock}-${type}`;
    
    let hash = 0;
    for (let i = 0; i < sessionString.length; i++) {
      const char = sessionString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash) % arrayLength;
  }

  /**
   * Get session-consistent wellness message
   */
  getRandomWellnessMessage() {
    const index = this.getSessionBasedIndex(this.wellnessMessages.length, 'wellness');
    return this.wellnessMessages[index];
  }

  /**
   * Get session-consistent engine message
   */
  getRandomEngineMessage() {
    const index = this.getSessionBasedIndex(this.engineMessages.length, 'engine');
    return this.engineMessages[index];
  }

  /**
   * Get session-consistent milestone message
   */
  getRandomMilestoneMessage() {
    const index = this.getSessionBasedIndex(this.milestoneMessages.length, 'milestone');
    return this.milestoneMessages[index];
  }

  /**
   * Get usage data from localStorage
   */
  getUsageData() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : { requests: [] };
    } catch (error) {
      console.warn('Failed to parse rate limit data from localStorage:', error);
      return { requests: [] };
    }
  }

  /**
   * Save usage data to localStorage
   */
  saveUsageData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save rate limit data to localStorage:', error);
    }
  }

  /**
   * Clean expired entries from usage data
   */
  cleanExpiredEntries(requests, windowSize) {
    const cutoff = this.now() - windowSize;
    return requests.filter(timestamp => timestamp > cutoff);
  }

  /**
   * Record a new request attempt
   */
  recordRequest() {
    const data = this.getUsageData();
    const now = this.now();
    
    // Clean expired entries
    data.requests = this.cleanExpiredEntries(data.requests, this.config.window);
    
    // Add new request
    data.requests.push(now);
    
    this.saveUsageData(data);
  }

  /**
   * Check if request would exceed limits
   */
  checkLimits() {
    const data = this.getUsageData();
    const now = this.now();
    
    // Clean expired entries
    const hourlyRequests = this.cleanExpiredEntries(data.requests || [], this.config.window);
    
    // Check hourly limit
    const hourlyExceeded = hourlyRequests.length >= this.config.userLimit;
    
    // Calculate reset times
    const oldestHourly = hourlyRequests.length > 0 ? Math.min(...hourlyRequests) : now;
    const hourlyResetTime = oldestHourly + this.config.window;
    
    return {
      allowed: !hourlyExceeded,
      hourly: {
        exceeded: hourlyExceeded,
        used: hourlyRequests.length,
        limit: this.config.userLimit,
        remaining: Math.max(0, this.config.userLimit - hourlyRequests.length),
        resetTime: hourlyResetTime,
        resetIn: Math.max(0, hourlyResetTime - now)
      }
    };
  }

  /**
   * Get usage info for display
   */
  getUsageInfo() {
    const data = this.getUsageData();
    const now = this.now();
    
    // Clean expired entries
    const hourlyRequests = this.cleanExpiredEntries(data.requests || [], this.config.window);
    
    const hourlyUsed = hourlyRequests.length;
    
    // Calculate next reset times
    const oldestHourly = hourlyRequests.length > 0 ? Math.min(...hourlyRequests) : now;
    
    const hourlyResetIn = hourlyRequests.length > 0 ? 
      Math.max(0, oldestHourly + this.config.window - now) : 0;
    
    return {
      hourly: {
        used: hourlyUsed,
        limit: this.config.userLimit,
        remaining: Math.max(0, this.config.userLimit - hourlyUsed),
        resetIn: hourlyResetIn
      }
    };
  }

  /**
   * Format time remaining for display
   */
  formatTimeRemaining(milliseconds) {
    if (milliseconds <= 0) return 'Available now';
    
    const seconds = Math.ceil(milliseconds / 1000);
    
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    
    const hours = Math.ceil(minutes / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  /**
   * Get user-friendly status message
   */
  getStatusMessage() {
    const limits = this.checkLimits();
    
    if (limits.allowed) {
      const info = this.getUsageInfo();
      const engineMessage = this.getRandomEngineMessage();
      const milestoneMessage = this.getRandomMilestoneMessage();
      return {
        type: 'success',
        message: `🚀 ${info.hourly.remaining} powerful assessments ready for you this hour!`,
        subtitle: `${engineMessage}. ${milestoneMessage} ✨`
      };
    }
    
    if (limits.hourly.exceeded) {
      const resetTime = this.formatTimeRemaining(limits.hourly.resetIn);
      const engineMessage = this.getRandomEngineMessage();
      const wellnessMessage = this.getRandomWellnessMessage();
      const milestoneMessage = this.getRandomMilestoneMessage();
      return {
        type: 'info',
        message: `✨ Please let our servers breath for ${resetTime}! ${engineMessage}`,
        subtitle: `Perfect timing to maximize your potential: ${wellnessMessage.toLowerCase()}. ${milestoneMessage} 🚀`
      };
    }
    
    
    return {
      type: 'info',
      message: '✨ Ready to assess your next company!',
      subtitle: `Our engines are warmed up and ready to go`
    };
  }

  /**
   * Get a short message suitable for toast notifications
   */
  getToastMessage() {
    const statusMessage = this.getStatusMessage();
    if (statusMessage.type === 'error' || statusMessage.type === 'warning') {
      return statusMessage.message; // Already friendly and concise
    }
    return statusMessage.message;
  }

  /**
   * Handle backend rate limit response
   */
  handleBackendRateLimit(response) {
    if (response.status === 429) {
      return response.json().then(data => ({
        blocked: true,
        type: 'backend',
        message: data.user_message || '🐎 Hold your horses! We\'re still tuning our engines for better performance.',
        retryAfter: data.details?.retry_after || 3600
      }));
    }
    return { blocked: false };
  }

  /**
   * Clear usage data (for testing or reset)
   */
  clearUsageData() {
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Update configuration (from backend response)
   */
  updateConfig(config) {
    if (config.user_limit) this.config.userLimit = config.user_limit;
    if (config.window) this.config.window = config.window * 1000; // Convert to milliseconds
  }

  /**
   * Get current configuration (for debugging)
   */
  getConfig() {
    return {
      ...this.config,
      environmentVariables: {
        userLimit: process.env.REACT_APP_USER_RATE_LIMIT,
        ipLimit: process.env.REACT_APP_IP_RATE_LIMIT,
        windowSeconds: process.env.REACT_APP_RATE_LIMIT_WINDOW_SECONDS
      }
    };
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();
export default rateLimitService;